<template>
    <div
        ref="trackview"
        class="listener-track-library"
        @scroll.passive="listenBottomOut"
    >
        <section class="listener-track-library__hero">
            <div class="listener-track-library__header">
                <div>
                    <div class="listener-track-library__eyebrow">{{ $tt('歌曲馆') }}</div>
                    <h1 class="listener-track-library__title">{{ heroTitle }}</h1>
                </div>
                <button
                    class="listener-track-library__refresh"
                    :disabled="loading"
                    :title="$tt('刷新')"
                    @click="refreshCurrentPage"
                >
                <svg t="1781507668775" class="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="2332" width="250" height="250"><path d="M797.538462 699.648h-27.569231a319.744 319.744 0 0 0 61.873231-188.908308A320.157538 320.157538 0 0 0 512.059077 190.976a39.384615 39.384615 0 0 0-39.384615 39.384615 39.384615 39.384615 0 0 0 39.384615 39.384616 241.289846 241.289846 0 0 1 241.014154 240.994461 240.955077 240.955077 0 0 1-46.020923 141.469539v-44.228923a39.384615 39.384615 0 0 0-39.384616-39.384616 39.384615 39.384615 0 0 0-39.384615 39.384616v131.052307a39.384615 39.384615 0 0 0 39.384615 39.384616h129.969231a39.384615 39.384615 0 0 0 39.384615-39.384616 39.384615 39.384615 0 0 0-39.483076-39.384615z" fill="#333333" p-id="2333"></path><path d="M512 754.235077a241.250462 241.250462 0 0 1-240.994462-240.974769 240.817231 240.817231 0 0 1 46.020924-141.371077v38.006154a39.384615 39.384615 0 0 0 39.384615 39.384615 39.384615 39.384615 0 0 0 39.384615-39.384615V278.843077a39.384615 39.384615 0 0 0-39.384615-39.384615h-129.969231a39.384615 39.384615 0 0 0-39.384615 39.384615 39.384615 39.384615 0 0 0 39.384615 39.384615h32.393846a319.369846 319.369846 0 0 0-66.619077 194.953846 320.118154 320.118154 0 0 0 319.763693 319.744 39.384615 39.384615 0 0 0 39.384615-39.384615A39.384615 39.384615 0 0 0 512 754.235077z" fill="#333333" p-id="2334"></path></svg>
                </button>
            </div>

            <div class="listener-track-library__tabs">
                <button
                    v-for="{ value, text } in options"
                    :key="value"
                    :class="['listener-track-library__tab', sortValue === value ? 'is-active' : '']"
                    @click="switchSort(value)"
                >
                    {{ $tt(text) }}
                </button>
            </div>

            <div class="listener-track-library__summary">
                <div class="listener-track-library__summary-kicker">{{ $tt('随手开播') }}</div>
                <div class="listener-track-library__summary-title">
                    {{ summaryTitle }}
                </div>
                <div class="listener-track-library__summary-sub">
                    {{ summarySubtitle }}
                </div>
                <div v-if="albums.length" class="listener-track-library__actions">
                    <button class="listener-track-library__action listener-track-library__action--primary" @click="playAll">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M8 5.14v13.72a1 1 0 0 0 1.5.86l10-6.86a1 1 0 0 0 0-1.72l-10-6.86A1 1 0 0 0 8 5.14z"></path>
                        </svg>
                        <span>{{ $tt('播放全部') }}</span>
                    </button>
                    <button class="listener-track-library__action" @click="playRandom">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M17 3h4v4h-2V6.41l-3.29 3.3-1.42-1.42L17.59 5H17V3zM3 7h5l3.29 3.29-1.42 1.42L7.17 9H3V7zm18 10v4h-4v-2h.59l-3.3-3.29 1.42-1.42 3.29 3.3V17H21zM3 15h4.17l10-10H21V3h-4v2h.59L7.17 15H3v2z"></path>
                        </svg>
                        <span>{{ $tt('打乱') }}</span>
                    </button>
                </div>
            </div>
        </section>

        <section v-if="albums.length" class="listener-track-library__section">
            <ListenerTrackList
                :tracks="albums"
                index-field="index"
                show-image
            />
        </section>

        <section v-else-if="!loading" class="listener-track-library__empty">
            <div class="listener-track-library__empty-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
                    <path d="M9 18V5l12-2v13"></path>
                    <circle cx="6" cy="18" r="3"></circle>
                    <circle cx="18" cy="16" r="3"></circle>
                </svg>
            </div>
            <h2>{{ $tt('这里还没有声音') }}</h2>
            <p>{{ emptyText }}</p>
        </section>

        <section v-if="loading" class="listener-track-library__state">
            <div class="listener-track-library__loading"></div>
            <p>{{ $tt('正在加载歌曲...') }}</p>
        </section>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import ListenerTrackList from './ListenerTrackList.vue'

export default {
    name: 'ListenerTrackLibrary',
    components: {
        ListenerTrackList
    },
    data() {
        return {
            albums: [],
            loading: false,
            offset: 0,
            hasMore: true,
            curScrollTop: 0,
            watchSearchTerm: null,
            searchTimeout: null
        }
    },
    computed: {
        ...mapGetters(['getSearchTerm', 'trackId']),
        options() {
            return [
                { text: '最近添加', value: 'recently-added' },
                { text: '最近播放', value: 'recently-played' },
                { text: '最多播放', value: 'most-played' },
                { text: 'A-Z排序', value: 'a-z' }
            ]
        },
        sortValue() {
            return this.$route.params.sort
        },
        heroTitle() {
            const titleMap = {
                'recently-added': this.$tt('刚收进来的新歌'),
                'recently-played': this.$tt('最近反复想听'),
                'most-played': this.$tt('循环最多的旋律'),
                'a-z': this.$tt('按字母慢慢翻')
            }
            return titleMap[this.sortValue] || this.$tt('歌曲列表')
        },
        summaryTitle() {
            if (this.getSearchTerm) {
                return this.$tt('找到 {count} 首相关歌曲', { count: this.albums.length })
            }
            return this.$tt('已经整理 {count} 首歌', { count: this.albums.length })
        },
        summarySubtitle() {
            if (this.getSearchTerm) {
                return this.$tt('和 “{keyword}” 有关的结果会继续出现在这里。', { keyword: this.getSearchTerm })
            }
            const textMap = {
                'recently-added': this.$tt('刚入库的新声音排在前面，适合直接看看最近收了什么。'),
                'recently-played': this.$tt('把最近点开过的歌聚在一起，接着上次的情绪继续听。'),
                'most-played': this.$tt('这些歌被你放得最多，适合一键回到熟悉的节奏。'),
                'a-z': this.$tt('按字母排序慢慢找，翻歌时会更清楚。')
            }
            return textMap[this.sortValue] || this.$tt('把常听的歌整理得更顺手一些。')
        },
        emptyText() {
            if (this.getSearchTerm) {
                return this.$tt('没有找到相关歌曲，换个关键词再试试。')
            }
            return this.$tt('这里暂时没有歌曲，稍后再回来看看。')
        }
    },
    created() {
        this.resetAndLoad()
        this.watchSearchTerm = this.$watch('getSearchTerm', this.loadSearchTimeout)
    },
    activated() {
        this.$nextTick(() => {
            if (this.$refs.trackview) {
                this.$refs.trackview.scrollTop = this.curScrollTop || 0
            }
        })
        this.startWatching()
    },
    deactivated() {
        this.stopWatching()
    },
    beforeDestroy() {
        this.stopWatching()
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout)
            this.searchTimeout = null
        }
    },
    methods: {
        startWatching() {
            if (!this.watchSearchTerm) {
                this.watchSearchTerm = this.$watch('getSearchTerm', this.loadSearchTimeout)
            }
        },
        stopWatching() {
            if (this.watchSearchTerm) {
                this.watchSearchTerm()
                this.watchSearchTerm = null
            }
        },
        resetState() {
            this.offset = 0
            this.albums = []
            this.hasMore = true
            this.curScrollTop = 0
            if (this.$refs.trackview) {
                this.$refs.trackview.scrollTop = 0
            }
        },
        resetAndLoad() {
            this.resetState()
            if (this.getSearchTerm) {
                return this.loadSearch()
            }
            return this.loadMore()
        },
        refreshCurrentPage() {
            if (this.loading) {
                return
            }
            return this.resetAndLoad()
        },
        loadMore() {
            if (this.loading || !this.hasMore || this.getSearchTerm) {
                return
            }
            this.loading = true
            return this.$api.subsonic.getTracksByGenre(this.sortValue, 50, this.offset).then(albums => {
                this.albums.push(...albums)
                this.offset += albums.length
                this.hasMore = albums.length > 0
                this.loading = false
            }).catch(() => {
                this.loading = false
            })
        },
        loadSearch() {
            if (this.getSearchTerm === '') {
                this.resetState()
                return this.loadMore()
            }
            this.loading = true
            this.offset = 0
            this.hasMore = true
            return this.$api.subsonic.searchTracksByGenre('search', this.getSearchTerm, 30, this.offset).then(albums => {
                this.albums = albums
                this.offset += albums.length
                this.hasMore = albums.length > 0
                this.loading = false
            }).catch((err) => {
                this.loading = false
                if (err.message === 'Wrong username or password.') {
                    this.$myMsg.notify({
                        title: this.$tt('查询失败！'),
                        content: this.$tt('请在后台管理创建一个subsonic密码，刷新页面试试吧'),
                        type: 'error',
                        time: 5000,
                        url: '/admin/user/userprofile/'
                    })
                } else {
                    this.$myMsg.notify({
                        title: this.$tt('查询失败！'),
                        content: err.messagee ? err.message : this.$tt('创建失败'),
                        type: 'error',
                        time: 5000
                    })
                }
            })
        },
        loadSearchTimeout() {
            if (this.searchTimeout) {
                clearTimeout(this.searchTimeout)
            }
            this.searchTimeout = setTimeout(() => {
                this.searchTimeout = null
                this.resetAndLoad()
            }, 500)
        },
        listenBottomOut() {
            if (!this.hasMore || this.loading || this.getSearchTerm || !this.$refs.trackview) {
                return
            }
            const scroller = this.$refs.trackview
            const scrollTop = scroller.scrollTop
            const scrollHeight = scroller.scrollHeight
            const clientHeight = scroller.clientHeight
            this.curScrollTop = scrollTop
            if (scrollTop + clientHeight >= scrollHeight - 120) {
                this.loadMore()
            }
        },
        switchSort(value) {
            if (value === this.sortValue) {
                return
            }
            this.$router.push({ path: '/tracks/' + value })
        },
        playTrack(index) {
            if (this.albums[index].id === this.trackId) {
                return this.$store.dispatch('playPause')
            }
            return this.$store.dispatch('playTrackList', {
                tracks: this.albums,
                index
            })
        },
        playAll() {
            if (!this.albums.length) {
                return
            }
            return this.$store.dispatch('playTrackList', {
                tracks: this.albums,
                index: 0
            })
        },
        playRandom() {
            if (!this.albums.length) {
                return
            }
            const index = Math.floor(Math.random() * this.albums.length)
            return this.playTrack(index)
        }
    }
}
</script>

<style scoped>
.listener-track-library {
    height: calc(100dvh - 12.25rem);
    padding: 0.1rem 0 0.25rem;
    box-sizing: border-box;
    overflow-y: auto;
}

.listener-track-library__hero {
    display: flex;
    flex-direction: column;
    gap: 0.9rem;
}

.listener-track-library__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
}

.listener-track-library__eyebrow {
    font-size: 0.82rem;
    font-weight: 700;
    color: #94a3b8;
}

.listener-track-library__title {
    margin-top: 0.18rem;
    font-size: 2rem;
    line-height: 1;
    font-weight: 800;
    color: #111827;
    letter-spacing: -0.04em;
}

.listener-track-library__refresh {
    width: 2.8rem;
    height: 2.8rem;
    border: 0;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.86);
    color: #0f172a;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 10px 24px rgba(148, 163, 184, 0.14);
}

.listener-track-library__refresh:disabled {
    opacity: 0.6;
}

.listener-track-library__refresh svg {
    width: 2rem;
    height: 1.5rem;
}

.listener-track-library__tabs {
    display: flex;
    gap: 0.55rem;
    overflow-x: auto;
    padding-bottom: 0.1rem;
    scrollbar-width: none;
}

.listener-track-library__tabs::-webkit-scrollbar {
    display: none;
}

.listener-track-library__tab {
    border: 0;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.72);
    color: #64748b;
    padding: 0.72rem 1rem;
    font-size: 0.88rem;
    font-weight: 700;
    white-space: nowrap;
    box-shadow: 0 8px 20px rgba(148, 163, 184, 0.08);
}

.listener-track-library__tab.is-active {
    background: #111827;
    color: #fff;
    box-shadow: 0 14px 30px rgba(17, 24, 39, 0.16);
}

.listener-track-library__summary {
    padding: 1.05rem;
    border-radius: 1.65rem;
    background: rgba(255, 255, 255, 0.76);
    box-shadow: 0 12px 28px rgba(148, 163, 184, 0.08);
    backdrop-filter: blur(16px);
}

.listener-track-library__summary-kicker {
    font-size: 0.74rem;
    font-weight: 700;
    color: #94a3b8;
}

.listener-track-library__summary-title {
    margin-top: 0.36rem;
    font-size: 1.15rem;
    line-height: 1.35;
    font-weight: 700;
    color: #111827;
}

.listener-track-library__summary-sub {
    margin-top: 0.42rem;
    font-size: 0.84rem;
    line-height: 1.6;
    color: #64748b;
}

.listener-track-library__actions {
    display: flex;
    gap: 0.7rem;
    margin-top: 0.95rem;
}

.listener-track-library__action {
    border: 0;
    border-radius: 999px;
    background: rgba(241, 245, 249, 0.92);
    color: #0f172a;
    min-height: 2.85rem;
    padding: 0 1rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.45rem;
    font-size: 0.88rem;
    font-weight: 700;
    box-shadow: 0 10px 24px rgba(148, 163, 184, 0.12);
}

.listener-track-library__action--primary {
    background: #111827;
    color: #fff;
}

.listener-track-library__action svg {
    width: 1rem;
    height: 1rem;
}

.listener-track-library__section {
    margin-top: 1rem;
}

.listener-track-library__empty,
.listener-track-library__state {
    margin-top: 1rem;
    padding: 1.4rem 1.1rem;
    border-radius: 1.6rem;
    background: rgba(255, 255, 255, 0.78);
    box-shadow: 0 12px 28px rgba(148, 163, 184, 0.08);
    text-align: center;
}

.listener-track-library__empty-icon {
    width: 3.5rem;
    height: 3.5rem;
    margin: 0 auto 0.85rem;
    border-radius: 1.2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(241, 245, 249, 0.92);
    color: #94a3b8;
}

.listener-track-library__empty-icon svg {
    width: 1.5rem;
    height: 1.5rem;
}

.listener-track-library__empty h2,
.listener-track-library__state p {
    color: #111827;
    font-weight: 700;
}

.listener-track-library__empty p {
    margin-top: 0.4rem;
    font-size: 0.84rem;
    line-height: 1.6;
    color: #64748b;
}

.listener-track-library__loading {
    width: 2.1rem;
    height: 2.1rem;
    margin: 0 auto 0.7rem;
    border-radius: 999px;
    border: 3px solid rgba(148, 163, 184, 0.22);
    border-top-color: #111827;
    animation: listener-track-library-spin 0.85s linear infinite;
}

@keyframes listener-track-library-spin {
    to {
        transform: rotate(360deg);
    }
}
</style>
